import React from 'react'
import { generatePath } from 'react-router-dom'
import Menu, { MenuProps } from 'components/menu'
import { MenuItem, MenuLabel, MenuList, MenuSeparator } from 'components/menu/items'
import { useAuth } from 'hooks/auth'
import {
  ADMIN_HOME,
  INTEGRATIONS,
  SECURITY,
  ADMIN_LOGS,
  ADMIN_ORGANISATIONS,
  ADMIN_CELERY,
  ADMIN_REDIS,
  ADMIN_LOCKS,
  ADMIN_CONVERSATIONS,
  ORGANISATION_DASHBOARD,
  TEAM_DASHBOARD,
  PROFILE
} from 'routes'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import {
  AdminPanel,
  Integrations,
  Organisation,
  Profile,
  Security,
  Audit,
  Integer,
  LogoutIcon,
  Lock,
  People
} from 'components/icons'
import { useApplicationStore } from 'hooks/application'
import Appearance from './appearance'

const UserMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { user, logout, isTeamManager } = useAuth()
  const { displayErrorMessage } = useApplicationStore()
  const isOrgAdmin = user?.adminOrganisations && user.adminOrganisations.length > 0
  const canShowTeamManagement = isTeamManager && !isOrgAdmin && user?.managedTeams && user.managedTeams.length > 0

  const signOut = async () => {
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
    try {
      await logout()
    } catch (error) {
      displayErrorMessage(error)
    }
  }

  const close = () => setMenuState(INITIAL_CONTEXT_MENU_STATE)

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
      <MenuList>
        <MenuLabel>Account</MenuLabel>
        <MenuItem
          icon={<Profile />}
          data-cy="profile-button"
          to={generatePath(PROFILE, { userId: user?.firebaseUserId ?? '#', tab: 'information' })}
          onClick={close}
        >
          Profile
        </MenuItem>
        {canShowTeamManagement && (
          <MenuItem
            icon={<People />}
            data-cy="manage-team-button"
            to={generatePath(TEAM_DASHBOARD, { teamId: user.managedTeams[0].publicId })}
            onClick={close}
          >
            Manage Team
          </MenuItem>
        )}
        <MenuItem
          icon={<Integrations />}
          data-cy="profile-button"
          data-tour="user-menu-integrations"
          to={INTEGRATIONS}
          onClick={close}
        >
          Integrations
        </MenuItem>
        <MenuItem icon={<Security />} data-cy="profile-button" to={SECURITY} onClick={close}>
          Settings & Security
        </MenuItem>

        <MenuSeparator />
        <MenuLabel>Appearance</MenuLabel>
        <Appearance />

        {user?.adminOrganisations && user.adminOrganisations.length > 0 && (
          <>
            <MenuSeparator />
            <MenuLabel>Organisations</MenuLabel>
            {user.adminOrganisations.map((org) => (
              <MenuItem
                key={org.publicId}
                icon={<Organisation />}
                to={generatePath(ORGANISATION_DASHBOARD, { organisationId: org.publicId })}
                onClick={close}
              >
                {org.name}
              </MenuItem>
            ))}
          </>
        )}

        {user?.isSuperAdmin && (
          <>
            <MenuSeparator />
            <MenuLabel>Admin Controls</MenuLabel>
            <MenuItem icon={<AdminPanel />} to={ADMIN_HOME} onClick={close}>
              Platform Users
            </MenuItem>
            <MenuItem icon={<Audit />} to={ADMIN_LOGS} onClick={close}>
              Platform Logs
            </MenuItem>
            <MenuItem icon={<Organisation />} to={ADMIN_ORGANISATIONS} onClick={close}>
              Organisations
            </MenuItem>
            <MenuItem icon={<Integer />} to={ADMIN_CELERY} onClick={close}>
              Celery Tasks
            </MenuItem>
            <MenuItem icon={<Integer />} to={ADMIN_REDIS} onClick={close}>
              Redis
            </MenuItem>
            <MenuItem icon={<Lock />} to={ADMIN_LOCKS} onClick={close}>
              Stuck DB Locks
            </MenuItem>
            <MenuItem icon={<Audit />} to={ADMIN_CONVERSATIONS} onClick={close}>
              AI Conversations
            </MenuItem>
          </>
        )}

        {/* Last, where most apps keep it, below everything else. */}
        <MenuSeparator />
        <MenuItem icon={<LogoutIcon />} variant="destructive" data-cy="signout-button" onClick={() => signOut()}>
          Sign Out
        </MenuItem>
      </MenuList>
    </Menu>
  )
}

export default UserMenu
